import { produce } from "immer";
import { mergeAll } from "ramda";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { fetchDocStructure } from "@hsl/core/documents";
import {
    fundsSchema,
    shareClassSchema,
} from "@hsl/core/fund-explorer/metaSchemas";
import {
    fetchFundExplorerData,
    indexData,
} from "@hsl/core/fund-explorer/services";
import {
    type TopLevelDataObj,
    type TopLevelDataType,
} from "@hsl/core/fund-explorer/types";
import { type Config, type PageSection } from "@hsl/core/fund-page/schemas";
import {
    getAllDataPartsFromConfig,
    getBannerImage,
} from "@hsl/core/fund-page/services";
import { generateLocalId } from "@hsl/lgim-explorer/src/store/helpers";

import { config as defaultConfig } from "../config/default.config";
import { processConfig } from "../config/helpers";
import {
    fetchInfoParts,
    fetchShareClasses,
    fetchTranslations,
    getFundExplorerUrl,
} from "./ssr-services";
import { type FundPageState } from "./types";

const useFundPageStore = create<FundPageState>()(
    devtools(
        (set, get) =>
            ({
                config: defaultConfig,
                rawData: undefined,
                indexedData: null,
                objectIds: { funds: [], shareClasses: [], listings: [] },
                refinedData: { funds: [], shareClasses: [], listings: [] },
                errors: [],
                messages: [],
                loadingFund: true,
                loadingContent: true,
                pageSections: [],
                pageSectionErrors: {},
                bannerImage: {
                    image: "",
                },
                activeShareClass: null,
                activeFund: null,
                isRefetching: false,
                translations: {},
                infoParts: {},
                shareClasses: [],
                hasBeenInitialised: false,
                isLiveData: false,
                async init(config, initialData) {
                    try {
                        if (initialData) {
                            set(
                                {
                                    ...get(),
                                    ...initialData,
                                    loadingFund: false,
                                    loadingContent: false,
                                    isLiveData: false,
                                },
                                false,
                                { type: "init: set static data" },
                            );

                            if (get().hasBeenInitialised) return;

                            if (typeof window === "undefined") return;

                            await get().fetchRawData(initialData.config);

                            get().indexData();

                            get().setData();

                            // stale-while-revalidate pattern
                            // it refetches the data in the background
                            const [docs] = await Promise.all([
                                fetchDocStructure(4, {
                                    base_url: config.base_url,
                                    audience: config.audienceId,
                                    share_classes: [
                                        initialData.activeShareClass.data.id,
                                    ],
                                    language: config.languageId,
                                }),
                                get().getDataParts([
                                    "overview",
                                    "performance",
                                    "performanceScenarios",
                                    "portfolioBreakdown",
                                    "prices",
                                ]),
                                get().getInfoParts(),
                                get().getTranslations(),
                            ]);

                            set(
                                produce((state: FundPageState) => {
                                    state.hasBeenInitialised = true;
                                    state.ribbonDocs = docs;
                                    state.isLiveData = true;
                                }),
                                false,
                                {
                                    type: "init: complete client-side fetch",
                                },
                            );

                            return;
                        }

                        if (get().hasBeenInitialised) return;

                        const processedConfig = processConfig(config);

                        await get().fetchRawData(processedConfig);

                        get().mergeConfigs(processedConfig);

                        void get().getBanner();

                        get().indexData();

                        get().setData();

                        const primaryFund = get().getFundById(
                            generateLocalId("fund")(processedConfig.fundId),
                        );
                        if (!primaryFund) throw Error("No primary fund");
                        get().setActiveFund(primaryFund.data.localId);

                        await Promise.all([
                            get().getShareClasses(),
                            get().getTranslations(),
                            get().getInfoParts(),
                        ]);

                        let activeShareClass;

                        if (config.shareClassId) {
                            activeShareClass = get().getShareClassById(
                                generateLocalId("shareclass")(
                                    config.shareClassId,
                                ),
                            );
                        } else {
                            console.warn(
                                "No shareclass ID found in route data - checking for activeShareClassISIN",
                            );
                            activeShareClass = get().getShareClassByISIN(
                                config.activeShareClassISIN,
                                config.activeScopeCode,
                            );
                        }

                        if (!activeShareClass) {
                            console.warn(
                                "No default share class at all - falling back to first share class",
                            );
                        }

                        const primaryShareclass =
                            activeShareClass?.id ??
                            activeShareClass?.data?.id ??
                            get().shareClasses[0]?.id;

                        const primaryShareclassId = primaryShareclass
                            ? generateLocalId("shareclass")(primaryShareclass)
                            : undefined;

                        await get().setActiveShareClass(
                            primaryShareclassId,
                            primaryFund,
                        );

                        set(
                            produce((state: FundPageState) => {
                                state.loadingContent = false;
                                state.hasBeenInitialised = true;
                                state.isLiveData = true;
                            }),
                            false,
                            { type: "set loading false" },
                        );
                    } catch (error) {
                        console.log(
                            "%cerror INIT fund page store",
                            "color: red; display: block; width: 100%;",
                            error,
                        );
                    }
                },
                getMeta(key) {
                    return get().rawData?.metadata[key];
                },
                getDataById(id) {
                    const { indexedData } = get();
                    return indexedData ? indexedData[id] : undefined;
                },
                getFundById(id) {
                    const { indexedData } = get();
                    if (!indexedData) {
                        return undefined;
                    }
                    const dataWithType = {
                        type: get().config.productType,
                        data: indexedData[id],
                    };
                    return fundsSchema.parse(dataWithType);
                },
                getShareClassById(id) {
                    const { indexedData } = get();
                    if (!indexedData) {
                        return undefined;
                    }
                    const dataWithType = {
                        type: get().config.productType,
                        data: indexedData[id],
                    };
                    const matchesSchema =
                        shareClassSchema.safeParse(dataWithType);
                    if (!matchesSchema.success) {
                        return null;
                    } else {
                        return matchesSchema.data;
                    }
                },
                getShareClassByISIN(isin = null, code = null) {
                    const data = get().indexedData;
                    if (data) {
                        return Array.from(Object.values(data))
                            .filter((item) => item.type === "shareclass")
                            .find(
                                (item) =>
                                    item?.data.isin === isin ||
                                    item?.data.shareclassISIN === isin ||
                                    item?.data.scopeCode === code,
                            );
                    }
                },
                async getDataParts(filter) {
                    const fund = get().activeFund;
                    if (!fund) throw Error("No active fund");

                    const shareClassId =
                        get().activeShareClass?.data.id ??
                        fund.data.primary_shareclass;
                    if (!shareClassId) throw Error("No active share class");

                    const pageSections = await getAllDataPartsFromConfig(
                        produce(get().config, (draft) => {
                            if (filter) {
                                draft.pageSections = draft.pageSections.filter(
                                    (section) => filter.includes(section.key),
                                );
                            }
                        }),
                        shareClassId,
                    );

                    set(
                        produce((state: FundPageState) => {
                            // need to merge to keep errors -> hook will handle change
                            const newSections = pageSections
                                .map((section) => {
                                    if (!section) {
                                        return undefined;
                                    }
                                    const currentSection =
                                        state.pageSections.find(
                                            (x) => x.key === section.key,
                                        );
                                    if (currentSection) {
                                        return mergeAll([
                                            currentSection,
                                            section,
                                        ]);
                                    }
                                    return section;
                                })
                                .filter(
                                    (x) => x !== undefined,
                                ) as PageSection[];

                            for (const section of newSections) {
                                const index = state.pageSections.findIndex(
                                    (x) => x.key === section.key,
                                );
                                if (index !== -1) {
                                    state.pageSections[index] = section;
                                } else {
                                    state.pageSections.push(section);
                                }
                            }
                        }),
                        false,
                        { type: "setting page sections" },
                    );
                },
                getFieldMetaForCode(type, codeName) {
                    const { rawData } = get();
                    const meta = rawData?.metadata
                        ? rawData.metadata[type]
                        : [];
                    return meta.find((obj) => obj.code_name === codeName);
                },
                async fetchRawData(processedConfig: Config) {
                    const rawData = await fetchFundExplorerData(
                        getFundExplorerUrl(processedConfig),
                    );
                    set(
                        produce((state: FundPageState) => {
                            state.rawData = rawData;
                        }),
                        false,
                        { type: "get raw data" },
                    );
                },
                setData() {
                    set(
                        produce((state: FundPageState) => {
                            if (state.indexedData) {
                                const getTypeIds = (type: TopLevelDataType) =>
                                    Object.entries(state.indexedData!)
                                        .filter(([_, obj]) => obj.type === type)
                                        .map(([id]) => id);
                                state.objectIds.funds = getTypeIds("fund");
                                state.objectIds.shareClasses =
                                    getTypeIds("shareclass");
                                state.objectIds.listings =
                                    getTypeIds("listing");
                                if (state.activeShareClass) {
                                    try {
                                        const refreshed =
                                            get().getShareClassById(
                                                state.activeShareClass.data
                                                    .localId,
                                            );
                                        if (refreshed) {
                                            state.activeShareClass = refreshed;
                                        }
                                    } catch (err) {
                                        console.error(err);
                                    }
                                }
                                if (state.activeFund) {
                                    try {
                                        const refreshed = get().getFundById(
                                            state.activeFund.data.localId,
                                        );
                                        if (refreshed) {
                                            state.activeFund = refreshed;
                                        }
                                    } catch (err) {
                                        console.error(err);
                                    }
                                }
                            }
                        }),
                        false,
                        { type: "init: set data" },
                    );
                },
                async setActiveShareClass(id, primaryFund) {
                    const config = get().config;

                    const defaultShareClassId = generateLocalId("shareclass")(
                        config.shareClassId ??
                            (primaryFund?.data["share_classes"][0] as number),
                    );
                    const active = get().getShareClassById(
                        (id ?? defaultShareClassId) as string,
                    );
                    if (!active) {
                        console.error(`No share class found with id ${id}`);
                        return;
                    }

                    set(
                        produce((state: FundPageState) => {
                            state.activeShareClass = active;
                            state.isRefetching = true;
                        }),
                        false,
                        { type: `set active share class: ${id}` },
                    );

                    set(
                        produce((state: FundPageState) => {
                            state.loadingFund = false;
                        }),
                        false,
                        { type: "finished loading fund" },
                    );

                    const [docs] = await Promise.all([
                        fetchDocStructure(4, {
                            base_url: config.base_url,
                            audience: config.audienceId,
                            share_classes: [active?.data.id],
                            language: config.languageId,
                        }),
                        get().getDataParts(),
                    ]);

                    set(
                        produce((state: FundPageState) => {
                            state.isRefetching = false;
                            state.ribbonDocs = docs;
                        }),
                        false,
                        { type: "finished refetching" },
                    );
                },
                setActiveFund(id) {
                    const active = get().getFundById(id);
                    if (!active) {
                        console.error(`No fund found with id ${id}`);
                        return;
                    }
                    document.title = `${
                        active.data.data.name.startsWith("Legal & General ")
                            ? active.data.data.name.slice(
                                  "Legal & General ".length,
                              )
                            : active.data.data.name
                    } | LGIM Fund Centre`;
                    set(
                        produce((state: FundPageState) => {
                            state.activeFund = active;
                        }),
                        false,
                        { type: `set active fund: ${id}` },
                    );
                },
                mergeConfigs(processedConfig: Config) {
                    set(
                        produce((state: FundPageState) => {
                            state.config = mergeAll([
                                defaultConfig,
                                processedConfig,
                            ]);
                        }),
                        false,
                        { type: "merge configs (processed/default)" },
                    );
                },
                indexData() {
                    set(
                        produce((state: FundPageState) => {
                            const { rawData, indexedData } = get();
                            state.indexedData = rawData
                                ? indexData(rawData)
                                : indexedData;
                        }),
                        false,
                        { type: "index data" },
                    );
                },
                setPageSectionError(key: string, error?: string) {
                    const current = get().pageSectionErrors[key];
                    if (current === error) return;
                    set(
                        produce((state: FundPageState) => {
                            if (error) {
                                state.pageSectionErrors[key] = error;
                            } else {
                                delete state.pageSectionErrors[key];
                            }
                        }),
                        false,
                        { type: `Error on section ${key}` },
                    );
                },
                async getTranslations() {
                    const data = await fetchTranslations(
                        get().config,
                        get().activeFund?.data.id,
                    );

                    set(
                        produce((state: FundPageState) => {
                            state.translations = data;
                        }),
                        false,
                        { type: "translations set" },
                    );
                },
                async getShareClasses() {
                    const scId = generateLocalId("shareclass");
                    const data = await fetchShareClasses(
                        get().config,
                        get().activeFund?.data.id,
                    );
                    set(
                        produce((state: FundPageState) => {
                            state.shareClasses = data.data.map(({ id }) =>
                                state.indexedData
                                    ? state.indexedData[scId(id)]
                                    : undefined,
                            ) as TopLevelDataObj[];
                        }),
                        false,
                        { type: "shareclasses set" },
                    );
                },
                async getBanner() {
                    const banner = await getBannerImage(get().config);

                    if (banner) {
                        set(
                            produce((state: FundPageState) => {
                                state.bannerImage = banner;
                            }),
                            false,
                            { type: "banner image set" },
                        );
                    } else {
                        console.error("No banner image found");
                    }
                },
                async getInfoParts() {
                    const data = await fetchInfoParts(
                        get().config,
                        get().activeFund?.data.id,
                    );

                    set(
                        produce((state: FundPageState) => {
                            state.infoParts = data;
                        }),
                        false,
                        { type: "info data set" },
                    );
                },
            }) as FundPageState,
        {
            name: "LGIM Fund Page",
            enabled: true,
        },
    ),
);

export default useFundPageStore;
